import React, { useState } from 'react';
import {
  getSignalWeights, setSignalWeights,
  getSeedingPrefs, setSeedingPrefs,
  getInterestProfile, clearInterestProfile, resetTagInProfile,
  getAlgorithmConfig, setAlgorithmConfig,
  SignalWeights, SeedingPrefs, AlgorithmConfig,
} from '../lib/signals';
import { COLORS, SPACING } from '../styles/theme';

export function FeedSettings() {
  const [weights, setWeightsState] = useState<SignalWeights>(getSignalWeights);
  const [prefs, setPrefsState] = useState<SeedingPrefs>(getSeedingPrefs);
  const [profile, setProfileState] = useState(getInterestProfile);
  const [algo, setAlgoState] = useState<AlgorithmConfig>(getAlgorithmConfig);

  function handleAlgoChange(key: keyof AlgorithmConfig, value: number) {
    const updated = { ...algo, [key]: value };
    setAlgoState(updated);
    setAlgorithmConfig(updated);
  }

  const refreshProfile = () => setProfileState(getInterestProfile());

  function handleWeightChange(key: keyof SignalWeights, value: number) {
    const updated = { ...weights, [key]: value };
    setWeightsState(updated);
    setSignalWeights(updated);
  }

  function handlePrefToggle(key: keyof SeedingPrefs) {
    const updated = { ...prefs, [key]: !prefs[key] };
    setPrefsState(updated);
    setSeedingPrefs(updated);
  }

  function handleClearProfile() {
    clearInterestProfile();
    refreshProfile();
  }

  function handleResetTag(tag: string) {
    resetTagInProfile(tag);
    refreshProfile();
  }

  const tagEntries = Object.entries(profile.tags).sort((a, b) => b[1] - a[1]);

  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: SPACING.sm }}>
      <span style={{ fontFamily: 'monospace', fontSize: '12px', color: COLORS.text }}>{label}</span>
      {children}
    </div>
  );

  const H = ({ children }: { children: React.ReactNode }) => (
    <h3 style={{
      color: COLORS.accent,
      margin: `${SPACING.lg} 0 ${SPACING.md}`,
      borderBottom: `1px solid ${COLORS.border}`,
      paddingBottom: SPACING.sm,
      fontFamily: 'monospace',
      fontSize: '12px',
      fontWeight: 700,
      fontStyle: 'italic'
    }}>
      {children}
    </h3>
  );

  return (
    <div style={{ padding: SPACING.lg }}>
      <H>Ranking Algorithm</H>
      <Row label="Gravity (freshness bias)">
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm }}>
          <input type="range" min={0.5} max={3.0} step={0.1} value={algo.gravity}
            onChange={e => handleAlgoChange('gravity', parseFloat(e.target.value))} style={{ width: 100 }} />
          <span style={{ fontSize: '11px', color: COLORS.textMuted, minWidth: 28, textAlign: 'right' }}>{algo.gravity.toFixed(1)}</span>
        </div>
      </Row>
      <p style={{ fontSize: '10px', color: COLORS.textMuted, fontFamily: 'monospace', margin: `0 0 ${SPACING.md} 0` }}>
        0.5 = evergreen · 1.8 = standard · 3.0 = only fresh
      </p>
      <Row label="Like weight">
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm }}>
          <input type="range" min={0} max={5} step={0.5} value={algo.likeWeight}
            onChange={e => handleAlgoChange('likeWeight', parseFloat(e.target.value))} style={{ width: 100 }} />
          <span style={{ fontSize: '11px', color: COLORS.textMuted, minWidth: 28, textAlign: 'right' }}>{algo.likeWeight}</span>
        </div>
      </Row>
      <Row label="Comment weight">
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm }}>
          <input type="range" min={0} max={5} step={0.5} value={algo.commentWeight}
            onChange={e => handleAlgoChange('commentWeight', parseFloat(e.target.value))} style={{ width: 100 }} />
          <span style={{ fontSize: '11px', color: COLORS.textMuted, minWidth: 28, textAlign: 'right' }}>{algo.commentWeight}</span>
        </div>
      </Row>
      <Row label="Max posts per author">
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm }}>
          <input type="range" min={1} max={10} step={1} value={algo.maxPerAuthor}
            onChange={e => handleAlgoChange('maxPerAuthor', parseInt(e.target.value))} style={{ width: 100 }} />
          <span style={{ fontSize: '11px', color: COLORS.textMuted, minWidth: 28, textAlign: 'right' }}>{algo.maxPerAuthor}</span>
        </div>
      </Row>

      <H>Signal Weights</H>
      {(Object.entries(weights) as [keyof SignalWeights, number][]).map(([key, val]) => (
        <Row key={key} label={key.replace(/_/g, ' ')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm }}>
            <input
              type="range" min={-5} max={5} step={0.5}
              value={val}
              onChange={e => handleWeightChange(key, parseFloat(e.target.value))}
              style={{ width: 100 }}
            />
            <span style={{ fontSize: '11px', color: COLORS.textMuted, minWidth: 28, textAlign: 'right' }}>{val}</span>
          </div>
        </Row>
      ))}

      <H>Engagement-Driven Seeding</H>
      <Row label="Auto-seed on engagement (master)">
        <input type="checkbox" checked={prefs.enabled} onChange={() => handlePrefToggle('enabled')} />
      </Row>
      {prefs.enabled && (
        <>
          {(['onPin', 'onLike', 'onSave', 'onReplay', 'onWatch'] as (keyof SeedingPrefs)[]).map(key => (
            <Row key={key} label={`  Seed on ${key.replace('on', '').toLowerCase()}`}>
              <input type="checkbox" checked={prefs[key] as boolean} onChange={() => handlePrefToggle(key)} />
            </Row>
          ))}
          <Row label="Remove seeds on dislike">
            <input type="checkbox" checked={prefs.removeSeedsOnDislike} onChange={() => handlePrefToggle('removeSeedsOnDislike')} />
          </Row>
        </>
      )}

      <H>Your Interest Profile</H>
      {tagEntries.length === 0 && (
        <p style={{ color: COLORS.textMuted, fontSize: '12px', fontFamily: 'monospace' }}>No signals recorded yet.</p>
      )}
      {tagEntries.slice(0, 30).map(([tag, score]) => (
        <div key={tag} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <span style={{ fontSize: '12px', fontFamily: 'monospace', color: score > 0 ? COLORS.text : COLORS.danger }}>{tag}</span>
          <div style={{ display: 'flex', gap: SPACING.sm, alignItems: 'center' }}>
            <span style={{ fontSize: '11px', color: COLORS.textMuted }}>{score.toFixed(1)}</span>
            <button
              onClick={() => handleResetTag(tag)}
              style={{
                background: 'transparent',
                border: `1px solid ${COLORS.border}`,
                color: COLORS.textMuted,
                padding: '1px 6px',
                cursor: 'pointer',
                fontSize: '10px',
                fontFamily: 'monospace'
              }}
            >
              ×
            </button>
          </div>
        </div>
      ))}
      {tagEntries.length > 0 && (
        <button
          onClick={handleClearProfile}
          style={{
            marginTop: SPACING.md,
            background: 'transparent',
            border: `1px solid ${COLORS.danger}`,
            color: COLORS.danger,
            padding: `${SPACING.xs} ${SPACING.sm}`,
            cursor: 'pointer',
            fontFamily: 'monospace',
            fontSize: '12px'
          }}
        >
          Reset entire interest profile
        </button>
      )}
    </div>
  );
}
